import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  OnDestroy,
} from '@angular/core';
import { environment } from '@environments/environment';
import { SharedService } from '@shared/services/shared.service';
import { AuthSsoService } from '@core/services/auth/auth-sso.service';

/** Matches design spacing; widget defaults to 24px in its shadow DOM. */
const LAUNCHER_EDGE_OFFSET = '16px';

@Component({
  selector: 'app-zendesk',
  imports: [],
  templateUrl: './zendesk.component.html',
  styleUrl: './zendesk.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZendeskComponent implements AfterViewInit, OnDestroy {
  private sharedService = inject(SharedService);
  private authSsoService = inject(AuthSsoService);
  private host = inject(ElementRef<HTMLElement>);

  private launcherOffsetIntervalId: ReturnType<typeof setInterval> | null = null;

  languageCode = this.sharedService.languageCode;
  fdGwToken = this.authSsoService.fdGWTokenSignal;

  keycloakEnvironment = environment.name === 'test' || environment.name === 'dev' ? 'preprod' : 'prod';

  ngAfterViewInit(): void {
    void this.loadChatWidget();
  }

  ngOnDestroy(): void {
    this.clearLauncherOffsetInterval();
  }

  private async loadChatWidget() {
    const { defineCustomElements } = await import('@myscania/myscania-chat-widget/loader');
    defineCustomElements();
    this.applyLauncherEdgeOffset();
  }

  /**
   * Launcher uses position:fixed with hardcoded 24px inset inside open shadow DOM.
   * No public offset API — adjust after the anchor mounts.
   */
  private applyLauncherEdgeOffset(): void {
    this.clearLauncherOffsetInterval();

    this.launcherOffsetIntervalId = setInterval(() => {
      const widget = this.host.nativeElement.querySelector('zendesk-widget');
      const anchor = widget?.shadowRoot?.querySelector('.icon-anchor--bottom-right');

      if (anchor instanceof HTMLElement) {
        anchor.style.right = LAUNCHER_EDGE_OFFSET;
        this.clearLauncherOffsetInterval();
      }
    }, 300);
  }

  private clearLauncherOffsetInterval(): void {
    if (this.launcherOffsetIntervalId !== null) {
      clearInterval(this.launcherOffsetIntervalId);
      this.launcherOffsetIntervalId = null;
    }
  }
}
